import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  Shield, Menu, X, LogOut, Home, Search, Map as MapIcon, Calendar, MessageSquare,
  User as UserIcon, Wallet, ListOrdered, Compass, UserPlus, Info, ChevronsLeft, ChevronsRight,
  ShieldAlert, Settings as SettingsIcon,
} from 'lucide-react';
import { getInitials } from '../lib/utils';
import Footer from './Footer';

const STORAGE_COLLAPSED = 'doorguard_sidebar_collapsed';

const Layout = ({ children }) => {
  const { user, isAuthenticated, logoutUser, isClient, isTrainer, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_COLLAPSED) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_COLLAPSED, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const handleLogout = () => { logoutUser(); navigate('/'); setDrawerOpen(false); };

  const sections = useMemo(() => {
    const arr = [
      { label: 'Discover', items: [
        { href: '/', label: 'Home', icon: Home },
        { href: '/find-trainer', label: 'Find a Trainer', icon: Search, primary: true },
        { href: '/map', label: 'Map', icon: MapIcon },
      ]},
    ];
    if (isAuthenticated) {
      arr.push({ label: 'My Account', items: [
        { href: '/dashboard/sessions', label: 'My Sessions', icon: Calendar },
        { href: '/messages', label: 'Messages', icon: MessageSquare },
        { href: '/profile', label: 'My Profile', icon: UserIcon },
        { href: '/account', label: 'Account Settings', icon: SettingsIcon },
      ]});
    }
    // Coach menu section removed (Jun 2026) — Earnings + My Listings now accessible from the coach dashboard.
    if (isAuthenticated && isTrainer) {
      arr.push({ label: 'Coach', items: [
        { href: '/earnings', label: 'Earnings', icon: Wallet },
      ]});
    }
    if (isAuthenticated && isAdmin) {
      arr.push({ label: 'Admin', items: [
        { href: '/admin', label: 'Admin Dashboard', icon: ShieldAlert },
      ]});
    }
    arr.push({ label: 'Info', items: [
      { href: '/how-it-works', label: 'How It Works', icon: Compass },
      { href: '/become-a-trainer', label: 'Become a Coach', icon: UserPlus },
      { href: '/about', label: 'About', icon: Info },
    ]});
    return arr;
  }, [isAuthenticated, isTrainer, isAdmin]);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const renderNavItem = (item, inDrawer = false) => {
    const Icon = item.icon;
    const active = isActive(item.href);
    const showLabel = !collapsed || inDrawer;
    const primary = item.primary;
    return (
      <Link
        key={item.href}
        to={item.href}
        onClick={() => inDrawer && setDrawerOpen(false)}
        className={`group flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
          active
            ? 'bg-[#C0A062]/15 text-[#C0A062] font-semibold'
            : primary
              ? 'text-[#C0A062] hover:bg-[#C0A062]/10'
              : 'text-zinc-300 hover:bg-zinc-800/60 hover:text-white'
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        title={collapsed ? item.label : undefined}
      >
        <Icon className={`w-5 h-5 shrink-0 ${primary && !active ? 'text-[#C0A062]' : ''}`} />
        {showLabel && <span className="truncate">{item.label}</span>}
      </Link>
    );
  };

  // Bottom tabs (mobile-only)
  const bottomTabs = [
    { href: '/', label: 'Home', icon: Home },
    { href: '/find-trainer', label: 'Find', icon: Search },
    { href: isAuthenticated ? '/dashboard/sessions' : '/login', label: 'Sessions', icon: Calendar },
    { href: isAuthenticated ? '/messages' : '/login', label: 'Messages', icon: MessageSquare },
    { href: isAuthenticated ? '/profile' : '/login', label: 'Profile', icon: UserIcon },
  ];

  const renderSidebar = (inDrawer = false) => (
    <div className={`flex flex-col h-full ${inDrawer ? 'w-full' : ''}`} data-testid={inDrawer ? 'mobile-drawer-nav' : 'sidebar-nav'}>
      <div className={`flex items-center ${collapsed && !inDrawer ? 'justify-center' : 'justify-between'} h-16 px-3 border-b border-zinc-800`}>
        <Link to="/" className="flex items-center gap-2 group shrink-0" onClick={() => inDrawer && setDrawerOpen(false)} data-testid="logo-link">
          <Shield className="w-7 h-7 text-[#C0A062] group-hover:scale-110 transition-transform" />
          {(!collapsed || inDrawer) && (
            <span className="text-base font-bold tracking-tight text-white">
              DOJO<span className="text-[#C0A062]">DASH</span>
            </span>
          )}
        </Link>
        {!inDrawer && (
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="hidden md:flex w-7 h-7 items-center justify-center rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="sidebar-collapse-btn"
          >
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
          </button>
        )}
        {inDrawer && (
          <button onClick={() => setDrawerOpen(false)} className="p-2 text-zinc-400 hover:text-white" aria-label="Close menu" data-testid="mobile-menu-close">
            <X className="w-6 h-6" />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-4">
        {sections.map((sec) => (
          <div key={sec.label}>
            {(!collapsed || inDrawer) && (
              <p className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">{sec.label}</p>
            )}
            <div className="space-y-0.5">
              {sec.items.map((item) => renderNavItem(item, inDrawer))}
            </div>
          </div>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-zinc-800">
        {isAuthenticated ? (
          <div className={`flex ${collapsed && !inDrawer ? 'flex-col items-center gap-2' : 'items-center gap-3'}`}>
            <Avatar className="w-9 h-9 border border-[#C0A062]/40">
              <AvatarImage src={user?.trainer_profile?.profile_image || user?.client_profile?.profile_image} />
              <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-xs">{getInitials(user?.full_name || 'U')}</AvatarFallback>
            </Avatar>
            {(!collapsed || inDrawer) && (
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{user?.full_name}</p>
                <button
                  onClick={handleLogout}
                  className="text-xs text-zinc-500 hover:text-red-400 inline-flex items-center gap-1"
                  data-testid="sign-out-btn"
                >
                  <LogOut className="w-3 h-3" /> Sign out
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className={`flex flex-col gap-2 ${collapsed && !inDrawer ? 'items-center' : ''}`}>
            {(!collapsed || inDrawer) ? (
              <>
                <Button onClick={() => { navigate('/register'); inDrawer && setDrawerOpen(false); }} className="w-full bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold" data-testid="sidebar-signup-btn">
                  Sign Up
                </Button>
                <Button variant="outline" onClick={() => { navigate('/login'); inDrawer && setDrawerOpen(false); }} className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="sidebar-login-btn">
                  Login
                </Button>
              </>
            ) : (
              <Button onClick={() => navigate('/login')} variant="ghost" size="sm" className="w-9 h-9 p-0 text-zinc-300" aria-label="Sign in">
                <UserIcon className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#09090B] noise-bg flex">
      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex fixed top-0 left-0 bottom-0 z-40 flex-col bg-[#0d0d10] border-r border-zinc-800 transition-all duration-300 ${
          collapsed ? 'w-[68px]' : 'w-[240px]'
        }`}
      >
        {renderSidebar(false)}
      </aside>

      {/* Mobile top bar (logo + theme + hamburger) */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 px-4 flex items-center justify-between bg-[#09090B]/90 backdrop-blur-md border-b border-zinc-800" data-testid="mobile-topbar">
        <Link to="/" className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-[#C0A062]" />
          <span className="text-sm font-bold tracking-tight text-white">DOJO<span className="text-[#C0A062]">DASH</span></span>
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="p-2 text-zinc-300 hover:text-white"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      {/* Mobile slide-in drawer */}
      <div
        className={`md:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-sm transition-opacity duration-200 ${
          drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setDrawerOpen(false)}
      />
      <aside
        className={`md:hidden fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-[#0F0F11] border-l border-zinc-800 shadow-2xl transform transition-transform duration-300 ease-out ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="mobile-drawer"
      >
        {renderSidebar(true)}
      </aside>

      {/* Main content offset */}
      <div className={`flex-1 flex flex-col min-w-0 ${collapsed ? 'md:ml-[68px]' : 'md:ml-[240px]'} transition-all duration-300`}>
        <main className="pt-14 md:pt-0 pb-20 md:pb-0 flex-1 min-w-0">{children}</main>
        <Footer />
      </div>

      {/* Mobile bottom tab bar */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0d0d10] border-t border-zinc-800 pb-[env(safe-area-inset-bottom)]"
        data-testid="bottom-tabbar"
      >
        <div className="grid grid-cols-5">
          {bottomTabs.map((tab) => {
            const Icon = tab.icon;
            const active = isActive(tab.href);
            return (
              <Link
                key={tab.label}
                to={tab.href}
                className={`flex flex-col items-center gap-0.5 py-2 text-[10px] ${
                  active ? 'text-[#C0A062]' : 'text-zinc-500 hover:text-zinc-200'
                }`}
                data-testid={`tab-${tab.label.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" />
                <span>{tab.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
